import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import Modal from '../ui/Modal'
import ProgressBar from '../ui/ProgressBar'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  Plus, Target, TrendingUp, TrendingDown, Minus, Bot, Send,
  Pencil, Trash2, RefreshCw, AlertTriangle, CheckCircle2, Clock,
  BarChart3, Activity, ChevronDown, ChevronRight, MessageSquare
} from 'lucide-react'

// ── KPI Status helpers ─────────────────────────────────────────────────────
const KPI_STATUS = {
  on_track: { label: 'En meta', color: 'text-green-600 bg-green-50 border-green-200', icon: TrendingUp },
  at_risk:  { label: 'En riesgo', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Minus },
  behind:   { label: 'Rezagado', color: 'text-red-600 bg-red-50 border-red-200', icon: TrendingDown },
  achieved: { label: 'Logrado', color: 'text-brand-700 bg-brand-50 border-brand-200', icon: CheckCircle2 },
}

function kpiProgress(kpi) {
  if (!kpi.target_value || kpi.target_value === 0) return 0
  const baseline = kpi.baseline_value ?? 0
  const range = kpi.target_value - baseline
  if (range === 0) return 100
  const current = kpi.current_value ?? baseline
  return Math.min(100, Math.max(0, Math.round(((current - baseline) / range) * 100)))
}

// ── KPI Card ───────────────────────────────────────────────────────────────
function KPICard({ kpi, objectives, isAdmin, onUpdate, onDelete }) {
  const [editOpen, setEditOpen] = useState(false)
  const [form, setForm] = useState({ ...kpi })
  const [saving, setSaving] = useState(false)
  const pct = kpiProgress(kpi)
  const StatusInfo = KPI_STATUS[kpi.status] || KPI_STATUS.on_track
  const StatusIcon = StatusInfo.icon

  async function save(e) {
    e.preventDefault()
    setSaving(true)
    await supabase.from('kpis').update({
      name: form.name, unit: form.unit,
      baseline_value: form.baseline_value || null,
      target_value: form.target_value || null,
      current_value: form.current_value || null,
      frequency: form.frequency, status: form.status,
      objective_id: form.objective_id || null,
      last_updated: new Date().toISOString(),
    }).eq('id', kpi.id)
    onUpdate({ ...kpi, ...form })
    setEditOpen(false)
    setSaving(false)
  }

  return (
    <>
      <div className={`card border ${StatusInfo.color} group`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">KPI</p>
            <h4 className="font-semibold text-gray-900 text-sm leading-tight">{kpi.name}</h4>
            {kpi.objective_id && (
              <p className="text-xs text-gray-400 mt-0.5">
                → {objectives.find(o => o.id === kpi.objective_id)?.name || 'Objetivo'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${StatusInfo.color}`}>
              <StatusIcon size={10} /> {StatusInfo.label}
            </span>
            {isAdmin && (
              <>
                <button onClick={() => setEditOpen(true)} className="p-1 text-gray-300 hover:text-brand-500 opacity-0 group-hover:opacity-100 transition-all">
                  <Pencil size={12} />
                </button>
                <button onClick={() => onDelete(kpi.id)} className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Values */}
        <div className="grid grid-cols-3 gap-2 mb-3 text-center">
          <div className="bg-white rounded-lg p-2 border border-gray-100">
            <p className="text-xs text-gray-400">Baseline</p>
            <p className="font-bold text-gray-700 text-sm">{kpi.baseline_value ?? '—'} <span className="text-xs font-normal">{kpi.unit}</span></p>
          </div>
          <div className="bg-white rounded-lg p-2 border border-gray-100">
            <p className="text-xs text-gray-400">Actual</p>
            <p className="font-bold text-gray-900 text-sm">{kpi.current_value ?? '—'} <span className="text-xs font-normal">{kpi.unit}</span></p>
          </div>
          <div className="bg-white rounded-lg p-2 border border-gray-100">
            <p className="text-xs text-gray-400">Meta</p>
            <p className="font-bold text-brand-700 text-sm">{kpi.target_value ?? '—'} <span className="text-xs font-normal">{kpi.unit}</span></p>
          </div>
        </div>

        {/* Progress */}
        <ProgressBar value={pct} size="sm" showLabel={false} />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{pct}% hacia la meta</span>
          <span className="capitalize">{kpi.frequency}</span>
        </div>
      </div>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar KPI">
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="label">Nombre del KPI *</label>
            <input className="input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Unidad</label>
              <input className="input" placeholder="%, USD, ton, unidades..." value={form.unit || ''}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
            </div>
            <div>
              <label className="label">Frecuencia</label>
              <select className="input" value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                {['daily','weekly','monthly','quarterly','annual'].map(v => (
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Baseline</label>
              <input type="number" step="any" className="input" value={form.baseline_value ?? ''}
                onChange={e => setForm(f => ({ ...f, baseline_value: e.target.value }))} />
            </div>
            <div>
              <label className="label">Valor actual</label>
              <input type="number" step="any" className="input" value={form.current_value ?? ''}
                onChange={e => setForm(f => ({ ...f, current_value: e.target.value }))} />
            </div>
            <div>
              <label className="label">Meta</label>
              <input type="number" step="any" className="input" value={form.target_value ?? ''}
                onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Objetivo relacionado</label>
              <select className="input" value={form.objective_id || ''} onChange={e => setForm(f => ({ ...f, objective_id: e.target.value || null }))}>
                <option value="">Sin objetivo</option>
                {objectives.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Estado</label>
              <select className="input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {Object.entries(KPI_STATUS).map(([v, k]) => <option key={v} value={v}>{k.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>
    </>
  )
}

// ── AI Execution Chat ──────────────────────────────────────────────────────
function ExecutionChat({ consultancyId, consultancy, objectives, kpis }) {
  const [messages, setMessages] = useState([{
    role: 'assistant',
    text: `¡Hola! Soy el **Agente de Seguimiento NVC**. Estoy aquí para ayudarte a monitorear la ejecución del plan estratégico.\n\nPuedo ayudarte con:\n- 📊 Análisis de KPIs y generación de OKRs\n- 🎯 Seguimiento de objetivos y pilares\n- ⚠️ Identificar riesgos y cuellos de botella\n- 💡 Recomendaciones de ajuste táctico\n\n**¿Qué quieres revisar hoy?** Puedes contarme cómo va la ejecución, compartirme datos de avance, o preguntarme por el estado de los KPIs.`,
  }])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const endRef = useRef(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function send(e) {
    e?.preventDefault()
    if (!input.trim() || loading) return
    const userMsg = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', text: userMsg }])
    setLoading(true)

    // Build context summary for the AI
    const objSummary = objectives.map(o =>
      `• ${o.name} (${o.status}, ${o.progress ?? 0}%)`
    ).join('\n')

    const kpiSummary = kpis.map(k =>
      `• ${k.name}: actual=${k.current_value ?? 'N/A'} ${k.unit ?? ''} / meta=${k.target_value ?? 'N/A'} ${k.unit ?? ''} (${KPI_STATUS[k.status]?.label || k.status})`
    ).join('\n')

    const systemPrompt = `Eres el Agente de Seguimiento Estratégico de Next Valley Consultants. Tu rol es ayudar a monitorear la ejecución del plan estratégico, generar KPIs y OKRs, identificar riesgos, y dar recomendaciones de ajuste táctico. Hablas en español, eres directo y orientado a resultados.

EMPRESA: ${consultancy?.client_company || consultancy?.name || 'Cliente NVC'}
INDUSTRIA: ${consultancy?.industry || 'No especificada'}

OBJETIVOS ESTRATÉGICOS ACTUALES:
${objSummary || 'Sin objetivos registrados aún'}

KPIs ACTUALES:
${kpiSummary || 'Sin KPIs registrados aún'}

Cuando el usuario comparte datos de avance, analiza la situación, detecta gaps vs metas, propón acciones concretas, y si es relevante sugiere OKRs con formato:
OBJETIVO: [enunciado]
KR1: [resultado clave medible]
KR2: [resultado clave medible]`

    try {
      const history = messages
        .filter(m => m.role !== 'system')
        .map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }))
      history.push({ role: 'user', content: userMsg })

      const { data, error } = await supabase.functions.invoke('ai-report', {
        body: {
          mode: 'chat',
          messages: history,
          systemPrompt,
          reportType: 'execution_tracking',
        }
      })

      if (error) throw error
      const reply = data?.report || '⚠️ Sin respuesta del servidor'
      setMessages(prev => [...prev, { role: 'assistant', text: reply }])
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: `⚠️ Error: ${err.message}` }])
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="card flex flex-col" style={{ height: '480px' }}>
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
        <div className="w-8 h-8 rounded-xl bg-brand-700 flex items-center justify-center">
          <Bot size={16} className="text-white" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">Agente de Seguimiento</p>
          <p className="text-xs text-gray-400">Análisis de ejecución, KPIs y OKRs</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            {m.role !== 'user' && (
              <div className="w-6 h-6 rounded-full bg-brand-700 flex items-center justify-center flex-shrink-0 mt-1">
                <Bot size={12} className="text-white" />
              </div>
            )}
            <div className={`max-w-[85%] rounded-2xl px-3 py-2.5 text-xs leading-relaxed ${
              m.role === 'user'
                ? 'bg-brand-700 text-white rounded-tr-none'
                : 'bg-gray-50 text-gray-800 border border-gray-100 rounded-tl-none'
            }`}>
              {m.role === 'user' ? (
                <p className="whitespace-pre-wrap">{m.text}</p>
              ) : (
                <div className="prose prose-xs max-w-none prose-headings:text-gray-900 prose-p:text-gray-700">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <div className="w-6 h-6 rounded-full bg-brand-700 flex items-center justify-center flex-shrink-0 mt-1">
              <Bot size={12} className="text-white" />
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-tl-none px-3 py-2.5">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <form onSubmit={send} className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
        <textarea
          className="input resize-none flex-1 text-xs py-2"
          rows={2}
          placeholder="Comparte datos de avance, pregunta por KPIs, pide análisis de riesgos..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }}}
          disabled={loading}
        />
        <button type="submit" disabled={loading || !input.trim()}
          className="btn-primary px-3 self-end">
          <Send size={14} />
        </button>
      </form>
    </div>
  )
}

// ── Objective Tracker Row ──────────────────────────────────────────────────
function ObjectiveTracker({ objective }) {
  const [expanded, setExpanded] = useState(false)
  const [updates, setUpdates] = useState([])
  const [loading, setLoading] = useState(false)
  const [newUpdate, setNewUpdate] = useState('')
  const [newPct, setNewPct] = useState(objective.progress ?? 0)
  const [savingUpdate, setSavingUpdate] = useState(false)
  const { profile } = useAuth()

  async function loadUpdates() {
    setLoading(true)
    const { data } = await supabase.from('execution_updates')
      .select('*, profiles(full_name)')
      .eq('entity_id', objective.id)
      .eq('entity_type', 'objective')
      .order('created_at', { ascending: false })
      .limit(5)
    setUpdates(data || [])
    setLoading(false)
  }

  async function addUpdate(e) {
    e.preventDefault()
    if (!newUpdate.trim()) return
    setSavingUpdate(true)
    await supabase.from('execution_updates').insert({
      consultancy_id: objective.consultancy_id,
      entity_type: 'objective',
      entity_id: objective.id,
      update_text: newUpdate,
      progress_pct: newPct,
      created_by: profile?.id,
    })
    await supabase.from('strategic_objectives').update({ progress: newPct }).eq('id', objective.id)
    setNewUpdate('')
    loadUpdates()
    setSavingUpdate(false)
  }

  const statusMap = { pending: 'Pendiente', in_progress: 'En progreso', at_risk: 'En riesgo', completed: 'Completado' }
  const statusColor = {
    pending: 'bg-gray-100 text-gray-500', in_progress: 'bg-blue-100 text-blue-700',
    at_risk: 'bg-amber-100 text-amber-700', completed: 'bg-green-100 text-green-700',
  }

  return (
    <div className="card">
      <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setExpanded(e => !e); if (!expanded) loadUpdates() }}>
        <div className="w-9 h-9 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center flex-shrink-0">
          <Target size={16} className="text-brand-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-gray-900">{objective.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[objective.status] || statusColor.pending}`}>
              {statusMap[objective.status] || 'Pendiente'}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <div className="flex-1 max-w-xs">
              <ProgressBar value={objective.progress ?? 0} size="sm" showLabel={false} />
            </div>
            <span className="text-xs text-gray-500 font-medium">{objective.progress ?? 0}%</span>
          </div>
        </div>
        {expanded ? <ChevronDown size={15} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
          {/* Update form */}
          <form onSubmit={addUpdate} className="space-y-2">
            <label className="text-xs font-medium text-gray-600">Registrar avance</label>
            <textarea className="input resize-none text-xs" rows={2}
              placeholder="Describe el avance, logros, o situación actual de este objetivo..."
              value={newUpdate} onChange={e => setNewUpdate(e.target.value)} />
            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-500">Progreso:</label>
              <input type="range" min="0" max="100" value={newPct}
                onChange={e => setNewPct(Number(e.target.value))}
                className="flex-1 accent-brand-600" />
              <span className="text-xs font-bold text-brand-700 w-8 text-right">{newPct}%</span>
              <button type="submit" disabled={savingUpdate || !newUpdate.trim()}
                className="btn-primary text-xs py-1.5 px-3">
                {savingUpdate ? 'Guardando...' : 'Registrar'}
              </button>
            </div>
          </form>

          {/* Updates history */}
          {loading && <p className="text-xs text-gray-400">Cargando historial...</p>}
          {updates.map(u => (
            <div key={u.id} className="text-xs bg-gray-50 rounded-xl px-3 py-2 space-y-1">
              <div className="flex items-center justify-between text-gray-400">
                <span>{u.profiles?.full_name || 'Usuario'}</span>
                <span>{new Date(u.created_at).toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric' })}</span>
              </div>
              <p className="text-gray-700">{u.update_text}</p>
              {u.progress_pct !== null && (
                <span className="inline-block bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full text-xs">
                  {u.progress_pct}% registrado
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main ExecutionView ─────────────────────────────────────────────────────
export default function ExecutionView({ consultancyId, consultancy }) {
  const { isAdmin } = useAuth()
  const [objectives, setObjectives] = useState([])
  const [kpis, setKpis] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeSection, setActiveSection] = useState('objectives') // 'objectives' | 'kpis' | 'chat'
  const [addKpiOpen, setAddKpiOpen] = useState(false)
  const [kpiForm, setKpiForm] = useState({ name: '', unit: '', baseline_value: '', target_value: '', current_value: '', frequency: 'monthly', status: 'on_track', objective_id: '' })
  const [savingKpi, setSavingKpi] = useState(false)

  useEffect(() => { loadData() }, [consultancyId])

  async function loadData() {
    setLoading(true)
    const [{ data: objs }, { data: kpisData }] = await Promise.all([
      supabase.from('strategic_objectives').select('*').eq('consultancy_id', consultancyId).order('obj_order'),
      supabase.from('kpis').select('*').eq('consultancy_id', consultancyId).order('created_at'),
    ])
    setObjectives(objs || [])
    setKpis(kpisData || [])
    setLoading(false)
  }

  async function addKpi(e) {
    e.preventDefault()
    setSavingKpi(true)
    const { data } = await supabase.from('kpis').insert({
      consultancy_id: consultancyId,
      name: kpiForm.name,
      unit: kpiForm.unit || null,
      baseline_value: kpiForm.baseline_value || null,
      target_value: kpiForm.target_value || null,
      current_value: kpiForm.current_value || null,
      frequency: kpiForm.frequency,
      status: kpiForm.status,
      objective_id: kpiForm.objective_id || null,
      last_updated: new Date().toISOString(),
    }).select().single()
    if (data) setKpis(prev => [...prev, data])
    setKpiForm({ name: '', unit: '', baseline_value: '', target_value: '', current_value: '', frequency: 'monthly', status: 'on_track', objective_id: '' })
    setAddKpiOpen(false)
    setSavingKpi(false)
  }

  async function deleteKpi(kpiId) {
    if (!confirm('¿Eliminar este KPI?')) return
    await supabase.from('kpis').delete().eq('id', kpiId)
    setKpis(prev => prev.filter(k => k.id !== kpiId))
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent" />
    </div>
  )

  const sections = [
    { id: 'objectives', label: 'Seguimiento de Objetivos', icon: Target },
    { id: 'kpis', label: 'KPIs', icon: BarChart3 },
    { id: 'chat', label: 'Agente IA', icon: Bot },
  ]

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900">Fase de Ejecución</h2>
          <p className="text-xs text-gray-500 mt-0.5">Seguimiento de objetivos, KPIs y análisis IA</p>
        </div>
        <button onClick={loadData} className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors">
          <RefreshCw size={15} />
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card text-center py-4">
          <p className="text-2xl font-bold text-brand-700">{objectives.length}</p>
          <p className="text-xs text-gray-500 mt-1">Objetivos estratégicos</p>
        </div>
        <div className="card text-center py-4">
          <p className="text-2xl font-bold text-green-600">
            {objectives.filter(o => o.status === 'completed').length}
          </p>
          <p className="text-xs text-gray-500 mt-1">Objetivos completados</p>
        </div>
        <div className="card text-center py-4">
          <p className="text-2xl font-bold text-amber-500">
            {objectives.filter(o => o.status === 'at_risk').length}
          </p>
          <p className="text-xs text-gray-500 mt-1">En riesgo</p>
        </div>
      </div>

      {/* Section nav */}
      <div className="flex gap-1 border-b border-gray-200">
        {sections.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveSection(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeSection === id
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Section content */}
      {activeSection === 'objectives' && (
        <div className="space-y-4">
          {objectives.length === 0 ? (
            <div className="card text-center py-10">
              <Target size={36} className="mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400">Sin objetivos registrados</p>
              <p className="text-xs text-gray-400 mt-1">Crea objetivos en el Plan Táctico primero</p>
            </div>
          ) : (
            objectives.map(obj => <ObjectiveTracker key={obj.id} objective={obj} />)
          )}
        </div>
      )}

      {activeSection === 'kpis' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            {isAdmin && (
              <button onClick={() => setAddKpiOpen(true)} className="btn-primary flex items-center gap-2 text-sm">
                <Plus size={14} /> Nuevo KPI
              </button>
            )}
          </div>
          {kpis.length === 0 ? (
            <div className="card text-center py-10">
              <BarChart3 size={36} className="mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400">Sin KPIs definidos aún</p>
              <p className="text-xs text-gray-400 mt-1">Agrega KPIs o pídele al Agente IA que los sugiera</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {kpis.map(k => (
                <KPICard key={k.id} kpi={k} objectives={objectives} isAdmin={isAdmin}
                  onUpdate={updated => setKpis(prev => prev.map(x => x.id === updated.id ? updated : x))}
                  onDelete={deleteKpi} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeSection === 'chat' && (
        <ExecutionChat
          consultancyId={consultancyId}
          consultancy={consultancy}
          objectives={objectives}
          kpis={kpis}
        />
      )}

      {/* Add KPI modal */}
      <Modal open={addKpiOpen} onClose={() => setAddKpiOpen(false)} title="Nuevo KPI">
        <form onSubmit={addKpi} className="space-y-3">
          <div>
            <label className="label">Nombre del KPI *</label>
            <input className="input" required value={kpiForm.name}
              onChange={e => setKpiForm(f => ({ ...f, name: e.target.value }))}
              placeholder="ej. Participación de mercado, CAGR de ventas..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Unidad</label>
              <input className="input" placeholder="%, USD, ton..." value={kpiForm.unit}
                onChange={e => setKpiForm(f => ({ ...f, unit: e.target.value }))} />
            </div>
            <div>
              <label className="label">Frecuencia de medición</label>
              <select className="input" value={kpiForm.frequency}
                onChange={e => setKpiForm(f => ({ ...f, frequency: e.target.value }))}>
                {['daily','weekly','monthly','quarterly','annual'].map(v => (
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Baseline</label>
              <input type="number" step="any" className="input" value={kpiForm.baseline_value}
                onChange={e => setKpiForm(f => ({ ...f, baseline_value: e.target.value }))} />
            </div>
            <div>
              <label className="label">Valor actual</label>
              <input type="number" step="any" className="input" value={kpiForm.current_value}
                onChange={e => setKpiForm(f => ({ ...f, current_value: e.target.value }))} />
            </div>
            <div>
              <label className="label">Meta</label>
              <input type="number" step="any" className="input" value={kpiForm.target_value}
                onChange={e => setKpiForm(f => ({ ...f, target_value: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Objetivo relacionado</label>
              <select className="input" value={kpiForm.objective_id}
                onChange={e => setKpiForm(f => ({ ...f, objective_id: e.target.value }))}>
                <option value="">Sin objetivo</option>
                {objectives.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Estado inicial</label>
              <select className="input" value={kpiForm.status}
                onChange={e => setKpiForm(f => ({ ...f, status: e.target.value }))}>
                {Object.entries(KPI_STATUS).map(([v, k]) => <option key={v} value={v}>{k.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setAddKpiOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingKpi} className="btn-primary flex-1">
              {savingKpi ? 'Creando...' : 'Crear KPI'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
